import React from 'react';
import { cn } from '../lib/utils';
import Wordmark from './Wordmark';

/* Page-shaped loading states, after the gig page's own skeleton: blocks shaped like the screen
   they stand in for, so the swap fills in rather than jumps; one pulse across the whole group
   (.s8-skel), not per block, since blocks breathing out of phase read as activity rather than
   waiting; the page's own surface colours; and a status line for screen readers. Styles live in
   index.css (.s8-skel, .s8-bone, .s8-bone-card). */

// `className` lands on the box that holds the bones, so spacing utilities (space-y-*) reach them.
export function PageSkeleton({ label, className, style, children }) {
  return (
    <div className="s8-skel" data-testid="page-skeleton">
      <span className="sr-only" role="status">{label}</span>
      <div aria-hidden className={className} style={style}>{children}</div>
    </div>
  );
}

const px = (v) => (typeof v === 'number' ? `${v}px` : v);

/* One block. `tone`: inner (text lines, default), card (larger shapes on the page ground), amber
   (money and the primary action). `r` is a radius token step: xs, sm, md, lg, pill. */
export function Bone({ w = '100%', h = 14, r = 'xs', tone = 'inner', className, style }) {
  return (
    <div
      className={cn('s8-bone', tone !== 'inner' && `s8-bone--${tone}`, className)}
      style={{ width: px(w), height: px(h), borderRadius: `var(--s8-radius-${r})`, ...style }}
    />
  );
}

// A card-shaped container for bones, on the card surface with the card border and radius.
export function BoneCard({ className, style, children }) {
  return <div className={cn('s8-bone-card', className)} style={style}>{children}</div>;
}

/* The whole app while it checks who is logged in, before any page or the real header exists: the
   header with the real wordmark (it needs no data) and bones where its controls go, then a page
   title and cards inside the same width and padding as the real content area (Layout.jsx). */
export function AppShellSkeleton() {
  return (
    <div className="min-h-screen flex flex-col" data-testid="app-shell-skeleton">
      <header className="nav-bar flex">
        <div className="max-w-[1400px] mx-auto w-full h-full flex items-center justify-between px-3 md:px-6 lg:px-8">
          <Wordmark className="h-[26px] md:h-[30px]" />
          <div className="s8-skel flex items-center gap-2" aria-hidden>
            <Bone w={92} h={28} r="md" />
            <Bone w={28} h={28} r="pill" />
          </div>
        </div>
      </header>
      <main className="flex-1 w-full">
        <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
          <PageSkeleton label="Loading STAGE8">
            <Bone w={200} h={30} tone="card" />
            <Bone w={120} h={14} className="mt-3" />
            <div className="space-y-3 mt-8 max-w-2xl">
              {[1, 2, 3].map((i) => (
                <BoneCard key={i} className="px-4 pt-5 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <Bone w="58%" h={20} />
                    <Bone w={64} h={20} tone="amber" />
                  </div>
                  <Bone w="44%" h={14} className="mt-3" />
                  <Bone w="36%" h={14} className="mt-2" />
                </BoneCard>
              ))}
            </div>
          </PageSkeleton>
        </div>
      </main>
    </div>
  );
}
